import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { API_BASE_URL } from '../../services/api-base';
import {
  ControlcliAttachment,
  ControlcliClient,
  ControlcliComment,
  ControlcliContact,
  ControlcliProposal,
  ControlcliProspect,
  ControlcliService,
  ControlcliServiceItem,
  ControlcliSurvey,
  ControlcliSurveyResponse
} from '../../services/controlcli.service';
import { UserSessionService } from '../../services/user-session.service';

@Component({
  selector: 'app-controlcli',
  templateUrl: './controlcli.component.html',
  styleUrls: ['./controlcli.component.scss']
})
export class ControlcliComponent implements OnInit {
  readonly prospectStages: ControlcliProspect['status'][] = [
    'NEW',
    'CONTACTED',
    'PROPOSAL_SENT',
    'NEGOTIATION',
    'SIGNATURE',
    'WON',
    'LOST'
  ];
  selectedTab: 'dashboard' | 'clients' | 'contacts' | 'prospects' | 'proposals' | 'services' | 'surveys' = 'dashboard';
  tenantId: number | null = null;
  userId: number | null = null;
  loading = false;

  clients: ControlcliClient[] = [];
  services: ControlcliServiceItem[] = [];
  prospects: ControlcliProspect[] = [];
  proposals: ControlcliProposal[] = [];
  surveys: ControlcliSurvey[] = [];
  surveyResponses: ControlcliSurveyResponse[] = [];
  attachments: ControlcliAttachment[] = [];
  comments: ControlcliComment[] = [];
  contacts: ControlcliContact[] = [];
  showClientForm = false;
  clientComments: ControlcliComment[] = [];
  prospectComments: ControlcliComment[] = [];
  commentFile: File | null = null;
  editingComment: ControlcliComment | null = null;
  private readonly fileHostUrl = `${API_BASE_URL}`;
  showContactForm = false;
  showContactModal = false;
  selectedContact: ControlcliContact | null = null;
  showProspectForm = false;
  showServiceForm = false;
  searchTermProspects = '';
  searchTermServices = '';
  showProspectDetail = false;
  showProposalForm = false;
  proposalFile: File | null = null;
  showCommentForm = false;
  showHistory = false;
  showProspectEdit = false;
  showProspectContactForm = false;
  draggingProspect: ControlcliProspect | null = null;
  dragOverStage: ControlcliProspect['status'] | null = null;
  historySearchTerm = '';
  servicePresentationFile: File | null = null;
  serviceEditPresentationFile: File | null = null;
  serviceFormSubmitted = false;
  serviceEditFormSubmitted = false;
  selectedService: ControlcliServiceItem | null = null;
  editingService = false;

  selectedClient: ControlcliClient | null = null;
  selectedProspect: ControlcliProspect | null = null;

  clientForm: FormGroup;
  clientEditForm: FormGroup;
  clientFormSubmitted = false;
  clientEditFormSubmitted = false;
  editingClient = false;
  contactForm: FormGroup;
  contactEditForm: FormGroup;
  serviceForm: FormGroup;
  serviceEditForm: FormGroup;
  prospectForm: FormGroup;
  prospectDetailForm: FormGroup;
  prospectContactForm: FormGroup;
  proposalForm: FormGroup;
  surveyForm: FormGroup;
  commentForm: FormGroup;
  searchTermContacts = '';

  searchTerm = '';
  segmentOptions = [
    'Fintech',
    'Tecnologia',
    'Saúde',
    'Educação',
    'Varejo',
    'Indústria',
    'Serviços',
    'Logística',
    'Agro',
    'Imobiliário'
  ];

  constructor(
    private fb: FormBuilder,
    private service: ControlcliService,
    private session: UserSessionService
  ) {
    this.clientForm = this.fb.group({
      cnpj: ['', [Validators.required, Validators.pattern(/^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/)]],
      name: ['', Validators.required],
      website: [''],
      segment: ['', Validators.required],
      description: [''],
      active: [null, Validators.required]
    });

    this.clientEditForm = this.fb.group({
      cnpj: ['', [Validators.required, Validators.pattern(/^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/)]],
      name: ['', Validators.required],
      website: [''],
      segment: ['', Validators.required],
      description: [''],
      active: [null, Validators.required]
    });

    this.contactForm = this.fb.group({
      client_id: ['', Validators.required],
      name: ['', Validators.required],
      area: [''],
      role: [''],
      email: ['', Validators.email],
      phone: ['']
    });

    this.contactEditForm = this.fb.group({
      client_id: ['', Validators.required],
      name: ['', Validators.required],
      area: [''],
      role: [''],
      email: ['', Validators.email],
      phone: ['']
    });

    this.serviceForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      product_link: [''],
      service_type: ['SERVICE', Validators.required],
      active: [true]
    });

    this.serviceEditForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      product_link: [''],
      service_type: ['SERVICE', Validators.required],
      active: [true]
    });

    this.prospectForm = this.fb.group({
      client_id: [''],
      service_id: ['', Validators.required],
      prospect_name: ['', Validators.required],
      need_description: ['', Validators.required],
      status: ['NEW', Validators.required],
      responsible_user_id: ['']
    });

    this.prospectDetailForm = this.fb.group({
      service_id: ['', Validators.required],
      need_description: ['', Validators.required],
      status: ['', Validators.required]
    });

    this.proposalForm = this.fb.group({
      prospect_id: ['', Validators.required],
      proposal_code: ['', Validators.required],
      description: ['', Validators.required],
      value_total: ['', Validators.required],
      value_monthly: ['', Validators.required],
      value_annual: ['', Validators.required],
      proposal_version: [1, Validators.required],
      valid_until: ['', Validators.required],
      status: ['SENT'],
      sent_by: ['']
    });
    this.disableProposalVersionControl();

    this.surveyForm = this.fb.group({
      title: ['', Validators.required],
      description: [''],
      active: [true]
    });

    this.commentForm = this.fb.group({
      reference_type: ['CLIENT', Validators.required],
      reference_id: ['', Validators.required],
      comment: ['', Validators.required]
    });

    this.prospectContactForm = this.fb.group({
      name: ['', Validators.required],
      area: [''],
      role: [''],
      email: ['', Validators.email],
      phone: ['']
    });
  }

  ngOnInit() {
    const user = this.session.getUser();
    this.tenantId = user?.tenant_id || null;
    this.userId = user?.id || null;
    if (this.tenantId) {
      this.loadAll();
      this.loadContacts();
    }
  }

  switchTab(tab: typeof this.selectedTab) {
    this.selectedTab = tab;
  }

  loadAll() {
    this.loadClients();
    this.loadServices();
    this.loadProspects();
    this.loadProposals();
    this.loadSurveys();
    this.loadSurveyResponses();
  }

  loadClients() {
    if (!this.tenantId) {
      return;
    }
    this.service.getClients(this.tenantId, this.searchTerm).subscribe({
      next: (rows) => (this.clients = rows || []),
      error: () => (this.clients = [])
    });
  }

  loadServices() {
    if (!this.tenantId) {
      return;
    }
    this.service.getServices(this.tenantId).subscribe({
      next: (rows) => (this.services = rows || []),
      error: () => (this.services = [])
    });
  }

  loadProspects() {
    if (!this.tenantId) {
      return;
    }
    this.service.getProspects(this.tenantId).subscribe({
      next: (rows) => (this.prospects = rows || []),
      error: () => (this.prospects = [])
    });
  }

  loadProposals() {
    if (!this.tenantId) {
      return;
    }
    this.service.getProposals(this.tenantId).subscribe({
      next: (rows) => (this.proposals = rows || []),
      error: () => (this.proposals = [])
    });
  }

  loadSurveys() {
    if (!this.tenantId) {
      return;
    }
    this.service.getSurveys(this.tenantId).subscribe({
      next: (rows) => (this.surveys = rows || []),
      error: () => (this.surveys = [])
    });
  }

  loadSurveyResponses() {
    if (!this.tenantId) {
      return;
    }
    this.service.getSurveyResponses(this.tenantId).subscribe({
      next: (rows) => (this.surveyResponses = rows || []),
      error: () => (this.surveyResponses = [])
    });
  }

  loadContacts() {
    if (!this.tenantId) {
      return;
    }
    this.service.getContacts(this.tenantId).subscribe({
      next: (rows) => (this.contacts = rows || []),
      error: () => (this.contacts = [])
    });
  }

  get filteredContacts() {
    const term = (this.searchTermContacts || '').toLowerCase();
    if (!term) {
      return this.contacts;
    }
    return this.contacts.filter((c) => {
      return (
        (c.name || '').toLowerCase().includes(term) ||
        (c.email || '').toLowerCase().includes(term) ||
        (this.clientNameById(c.client_id) || '').toLowerCase().includes(term)
      );
    });
  }

  clientNameById(id: number) {
    const client = this.clients.find((c) => c.id === id);
    return client ? client.name : '—';
  }

  get filteredProspects() {
    const term = (this.searchTermProspects || '').toLowerCase();
    if (!term) {
      return this.prospects;
    }
    return this.prospects.filter((p) => {
      return (
        (p.prospect_name || '').toLowerCase().includes(term) ||
        (p.need_description || '').toLowerCase().includes(term) ||
        (p.client_name || '').toLowerCase().includes(term) ||
        (p.service_name || '').toLowerCase().includes(term)
      );
    });
  }

  get filteredServices() {
    const term = (this.searchTermServices || '').toLowerCase();
    if (!term) {
      return this.services;
    }
    return this.services.filter((s) => (s.name || '').toLowerCase().includes(term) || (s.description || '').toLowerCase().includes(term));
  }

  submitClient() {
    this.clientFormSubmitted = true;
    if (!this.tenantId || !this.userId || !this.clientForm.valid) {
      this.clientForm.markAllAsTouched();
      return;
    }
    const payload = {
      ...this.clientForm.value,
      tenant_id: this.tenantId,
      created_by: this.userId,
      active: this.clientForm.value.active === 'ACTIVE' ? 1 : 0
    };
    this.service.createClient(payload).subscribe({
      next: () => {
        this.clientForm.reset({ active: null });
        this.clientFormSubmitted = false;
        this.showClientForm = false;
        this.loadClients();
      }
    });
  }

  onCnpjInput(event: Event) {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }
    const digits = input.value.replace(/\D/g, '').slice(0, 14);
    const formatted = this.formatCnpj(digits);
    this.clientForm.patchValue({ cnpj: formatted }, { emitEvent: false });
  }

  onCnpjEditInput(event: Event) {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }
    const digits = input.value.replace(/\D/g, '').slice(0, 14);
    const formatted = this.formatCnpj(digits);
    this.clientEditForm.patchValue({ cnpj: formatted }, { emitEvent: false });
  }

  private formatCnpj(digits: string) {
    if (!digits) {
      return '';
    }
    let value = digits;
    value = value.replace(/^(\d{2})(\d)/, '$1.$2');
    value = value.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3');
    value = value.replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4');
    value = value.replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d{1,2})$/, '$1.$2.$3/$4-$5');
    return value;
  }

  submitContact() {
    if (!this.tenantId || !this.userId || !this.contactForm.valid) {
      return;
    }
    const clientId = this.contactForm.value.client_id;
    if (!clientId) {
      return;
    }
    const payload = {
      ...this.contactForm.value,
      tenant_id: this.tenantId,
      created_by: this.userId
    };
    this.service.createContact(clientId, payload).subscribe({
      next: () => {
        this.contactForm.reset({ client_id: '', name: '', email: '' });
        this.showContactForm = false;
        this.loadContacts();
      }
    });
  }

  toggleContactDetail(contact: ControlcliContact) {
    if (this.selectedContact?.id === contact.id && this.showContactModal) {
      this.closeContactModal();
      return;
    }
    this.selectedContact = contact;
    this.contactEditForm.reset({
      client_id: contact.client_id,
      name: contact.name,
      area: contact.area || '',
      role: contact.role || '',
      email: contact.email || '',
      phone: contact.phone || ''
    });
    this.showContactModal = true;
  }

  closeContactModal() {
    this.showContactModal = false;
    this.selectedContact = null;
  }

  saveContactEdit() {
    if (!this.tenantId || !this.userId || !this.selectedContact || !this.contactEditForm.valid) {
      this.contactEditForm.markAllAsTouched();
      return;
    }
    const payload = {
      ...this.contactEditForm.value,
      tenant_id: this.tenantId
    };
    this.service.updateContact(this.selectedContact.id, payload).subscribe({
      next: () => {
        const updated = { ...this.selectedContact!, ...this.contactEditForm.value };
        this.contacts = this.contacts.map((c) => (c.id === updated.id ? { ...c, ...updated } : c));
        this.selectedContact = updated;
        this.closeContactModal();
      }
    });
  }

  submitService() {
    this.serviceFormSubmitted = true;
    if (!this.tenantId || !this.userId || !this.serviceForm.valid) {
      this.serviceForm.markAllAsTouched();
      return;
    }
    const formData = new FormData();
    formData.append('tenant_id', String(this.tenantId));
    formData.append('created_by', String(this.userId));
    formData.append('name', this.serviceForm.value.name);
    formData.append('description', this.serviceForm.value.description || '');
    formData.append('product_link', this.serviceForm.value.product_link || '');
    formData.append('service_type', this.serviceForm.value.service_type);
    formData.append('active', this.serviceForm.value.active ? '1' : '0');
    if (this.servicePresentationFile) {
      formData.append('presentation', this.servicePresentationFile);
    }
    this.service.createService(formData).subscribe({
      next: () => {
        this.serviceForm.reset({ service_type: 'SERVICE', active: true, product_link: '' });
        this.servicePresentationFile = null;
        this.serviceFormSubmitted = false;
        this.showServiceForm = false;
        this.loadServices();
      }
    });
  }

  onServiceFileSelected(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0] || null;
    if (file) {
      this.servicePresentationFile = file;
    }
    if (input) {
      input.value = '';
    }
  }

  onServiceDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onServiceFileDropped(event: DragEvent) {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0] || null;
    if (file) {
      this.servicePresentationFile = file;
    }
  }

  selectService(service: ControlcliServiceItem) {
    if (this.selectedService?.id === service.id) {
      this.selectedService = null;
      this.editingService = false;
      this.serviceEditPresentationFile = null;
      this.serviceEditFormSubmitted = false;
      return;
    }
    this.selectedService = service;
    this.editingService = false;
    this.serviceEditFormSubmitted = false;
    this.serviceEditPresentationFile = null;
    this.serviceEditForm.patchValue({
      name: service.name || '',
      description: service.description || '',
      product_link: service.product_link || '',
      service_type: service.service_type || 'SERVICE',
      active: !!service.active
    });
  }

  startServiceEdit() {
    if (!this.selectedService) {
      return;
    }
    this.editingService = true;
  }

  cancelServiceEdit() {
    if (!this.selectedService) {
      return;
    }
    this.editingService = false;
    this.serviceEditFormSubmitted = false;
    this.serviceEditPresentationFile = null;
    this.serviceEditForm.patchValue({
      name: this.selectedService.name || '',
      description: this.selectedService.description || '',
      product_link: this.selectedService.product_link || '',
      service_type: this.selectedService.service_type || 'SERVICE',
      active: !!this.selectedService.active
    });
  }

  saveServiceEdit() {
    if (!this.selectedService || !this.tenantId) {
      return;
    }
    this.serviceEditFormSubmitted = true;
    if (!this.serviceEditForm.valid) {
      this.serviceEditForm.markAllAsTouched();
      return;
    }
    const formData = new FormData();
    formData.append('tenant_id', String(this.tenantId));
    formData.append('name', this.serviceEditForm.value.name);
    formData.append('description', this.serviceEditForm.value.description || '');
    formData.append('product_link', this.serviceEditForm.value.product_link || '');
    formData.append('service_type', this.serviceEditForm.value.service_type);
    formData.append('active', this.serviceEditForm.value.active ? '1' : '0');
    if (this.serviceEditPresentationFile) {
      formData.append('presentation', this.serviceEditPresentationFile);
    }
    this.service.updateService(this.selectedService.id, formData).subscribe({
      next: (updated: any) => {
        this.selectedService = updated;
        const index = this.services.findIndex((item) => item.id === updated.id);
        if (index !== -1) {
          this.services[index] = updated;
        }
        this.editingService = false;
        this.serviceEditFormSubmitted = false;
        this.serviceEditPresentationFile = null;
        this.loadServices();
      }
    });
  }

  onServiceEditFileSelected(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0] || null;
    if (file) {
      this.serviceEditPresentationFile = file;
    }
    if (input) {
      input.value = '';
    }
  }

  onServiceEditDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onServiceEditFileDropped(event: DragEvent) {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0] || null;
    if (file) {
      this.serviceEditPresentationFile = file;
    }
  }

  submitProspect() {
    if (!this.tenantId || !this.userId || !this.prospectForm.valid) {
      return;
    }
    const payload = {
      ...this.prospectForm.value,
      tenant_id: this.tenantId,
      responsible_user_id: this.prospectForm.value.responsible_user_id || this.userId
    };
    this.service.createProspect(payload).subscribe({
      next: () => {
        this.prospectForm.reset({
          status: 'NEW',
          service_id: '',
          client_id: ''
        });
        this.loadProspects();
      }
    });
  }

  submitProposal() {
    if (!this.tenantId || !this.userId) {
      return;
    }
    if (!this.proposalForm.value.prospect_id && this.selectedProspect) {
      this.proposalForm.patchValue({ prospect_id: this.selectedProspect.id });
    }
    if (!this.proposalForm.valid) {
      return;
    }
    const raw = this.proposalForm.getRawValue();
    const payload = {
      ...raw,
      tenant_id: this.tenantId,
      sent_by: raw.sent_by || this.userId,
      value_total: this.parseCurrencyValue(raw.value_total),
      value_monthly: this.parseCurrencyValue(raw.value_monthly),
      value_annual: this.parseCurrencyValue(raw.value_annual),
      valid_until: raw.valid_until || null
    };
    this.service.createProposal(payload).subscribe({
      next: (created: any) => {
        if (created?.id && this.proposalFile) {
          const formData = new FormData();
          formData.append('tenant_id', String(this.tenantId));
          formData.append('uploaded_by', String(this.userId));
          formData.append('file', this.proposalFile);
          this.service.uploadProposalAttachment(created.id, formData).subscribe((att: any) => {
            this.addProposalAttachment(created.id, att);
            this.proposalFile = null;
          });
        }
        this.proposalForm.reset({
          status: 'SENT',
          value_total: '',
          value_monthly: '',
          value_annual: '',
          proposal_version: 1,
          prospect_id: ''
        });
        this.disableProposalVersionControl();
        this.loadProposals();
        if (this.selectedProspect) {
          this.loadProspectComments(this.selectedProspect.id);
        }
        this.showProposalForm = false;
      }
    });
  }

  toggleProposalForm() {
    this.showProposalForm = !this.showProposalForm;
    if (this.showProposalForm) {
      this.setProposalDefaults();
    }
  }

  private setProposalDefaults() {
    if (!this.tenantId) {
      return;
    }
    if (this.selectedProspect?.id) {
      const lastProposal = this.getLastProposalForProspect(this.selectedProspect.id);
      const nextVersion = this.getNextProposalVersion(this.selectedProspect.id);
      this.proposalForm.patchValue({
        proposal_version: nextVersion,
        proposal_code: lastProposal?.proposal_code || '',
        value_total: this.formatCurrencyValue(lastProposal?.value_total ?? null),
        value_monthly: this.formatCurrencyValue(lastProposal?.value_monthly ?? null),
        value_annual: this.formatCurrencyValue(lastProposal?.value_annual ?? null)
      });
      if (!lastProposal?.proposal_code) {
        this.fillNextProposalCode();
      }
      return;
    }
    this.fillNextProposalCode();
  }

  private getNextProposalVersion(prospectId: number) {
    const versions = this.proposals
      .filter((p) => p.prospect_id === prospectId)
      .map((p) => p.proposal_version || 0);
    const max = versions.length ? Math.max(...versions) : 0;
    return max + 1;
  }

  private getLastProposalForProspect(prospectId: number) {
    const proposals = this.proposals.filter((p) => p.prospect_id === prospectId);
    if (!proposals.length) {
      return null;
    }
    return proposals.sort((a, b) => {
      const aTime = a.sent_at ? new Date(a.sent_at).getTime() : 0;
      const bTime = b.sent_at ? new Date(b.sent_at).getTime() : 0;
      return bTime - aTime;
    })[0];
  }

  private fillNextProposalCode() {
    const currentCode = this.proposalForm.value.proposal_code;
    if (currentCode || !this.tenantId) {
      return;
    }
    this.service.getNextProposalCode(this.tenantId).subscribe({
      next: (data) => {
        this.proposalForm.patchValue({ proposal_code: data.proposal_code });
      }
    });
  }

  private disableProposalVersionControl() {
    this.proposalForm.get('proposal_version')?.disable({ emitEvent: false });
  }

  submitSurvey() {
    if (!this.tenantId || !this.userId || !this.surveyForm.valid) {
      return;
    }
    const payload = {
      ...this.surveyForm.value,
      tenant_id: this.tenantId,
      created_by: this.userId,
      active: this.surveyForm.value.active ? 1 : 0
    };
    this.service.createSurvey(payload).subscribe({
      next: () => {
        this.surveyForm.reset({ active: true });
        this.loadSurveys();
      }
    });
  }

  submitComment() {
    if (!this.tenantId || !this.userId || !this.commentForm.valid) {
      return;
    }
    const payload = {
      ...this.commentForm.value,
      tenant_id: this.tenantId,
      user_id: this.userId
    };
    this.service.addComment(payload).subscribe({
      next: (created: any) => {
        const targetId = payload.reference_id;
        this.refreshComments(payload.reference_type, targetId);
        if (created?.id && this.commentFile) {
          const formData = new FormData();
          formData.append('tenant_id', String(this.tenantId));
          formData.append('uploaded_by', String(this.userId));
          formData.append('file', this.commentFile);
          this.service.uploadCommentAttachment(created.id, formData).subscribe(() => {
            this.refreshComments(payload.reference_type as any, targetId);
          });
        }
        this.commentForm.patchValue({ comment: '' });
        this.commentFile = null;
      }
    });
  }

  onProspectDragStart(event: DragEvent, prospect: ControlcliProspect) {
    this.draggingProspect = prospect;
    if (event.dataTransfer) {
      event.dataTransfer.setData('text/plain', String(prospect.id));
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  onProspectDragEnd() {
    this.draggingProspect = null;
    this.dragOverStage = null;
  }

  onProspectDragOver(event: DragEvent, stage: ControlcliProspect['status']) {
    event.preventDefault();
    this.dragOverStage = stage;
  }

  onProspectDrop(event: DragEvent, stage: ControlcliProspect['status']) {
    event.preventDefault();
    const dragged = this.draggingProspect || this.getDraggedProspect(event);
    this.dragOverStage = null;
    if (!dragged) {
      return;
    }
    if (dragged.status === stage) {
      return;
    }
    this.moveProspectToStage(dragged, stage);
  }

  private getDraggedProspect(event: DragEvent) {
    const id = event.dataTransfer?.getData('text/plain');
    if (!id) {
      return null;
    }
    const numericId = Number(id);
    if (!Number.isFinite(numericId)) {
      return null;
    }
    return this.prospects.find((p) => p.id === numericId) || null;
  }

  private moveProspectToStage(prospect: ControlcliProspect, stage: ControlcliProspect['status']) {
    const previous = prospect.status;
    this.prospects = this.prospects.map((p) => (p.id === prospect.id ? { ...p, status: stage } : p));
    if (this.selectedProspect?.id === prospect.id) {
      this.selectedProspect = { ...this.selectedProspect, status: stage };
      this.prospectDetailForm.patchValue({ status: stage });
    }
    this.service.updateProspect(prospect.id, { status: stage }).subscribe({
      next: () => {
        this.logProspectMove(prospect.id, previous, stage);
      },
      error: () => {
        this.prospects = this.prospects.map((p) => (p.id === prospect.id ? { ...p, status: previous } : p));
        if (this.selectedProspect?.id === prospect.id) {
          this.selectedProspect = { ...this.selectedProspect, status: previous };
          this.prospectDetailForm.patchValue({ status: previous });
        }
      }
    });
  }

  private logProspectMove(prospectId: number, from: ControlcliProspect['status'], to: ControlcliProspect['status']) {
    if (!this.tenantId || !this.userId) {
      return;
    }
    const payload = {
      tenant_id: this.tenantId,
      reference_type: 'PROSPECT',
      reference_id: prospectId,
      user_id: this.userId,
      comment: `Movimentado de ${this.translateProspectStatus(from)} para ${this.translateProspectStatus(to)}.`
    };
    this.service.addComment(payload).subscribe({
      next: () => {
        if (this.selectedProspect?.id === prospectId) {
          this.loadProspectComments(prospectId);
        }
      }
    });
  }

  selectClient(client: ControlcliClient) {
    if (this.selectedClient?.id === client.id) {
      this.selectedClient = null;
      this.editingClient = false;
      this.commentFile = null;
      this.clientEditForm.reset({ active: null });
      this.commentForm.patchValue({ reference_type: 'CLIENT', reference_id: '', comment: '' });
      this.clientComments = [];
      return;
    }
    this.selectedClient = client;
    this.editingClient = false;
    this.clientEditFormSubmitted = false;
    this.clientEditForm.patchValue({
      cnpj: client.cnpj || '',
      name: client.name || '',
      website: client.website || '',
      segment: client.segment || '',
      description: client.description || '',
      active: client.active ? 'ACTIVE' : 'PROSPECT'
    });
    this.commentForm.patchValue({ reference_type: 'CLIENT', reference_id: client.id, comment: '' });
    this.contactForm.patchValue({ client_id: client.id });
    this.loadClientComments(client.id);
  }

  toggleClientForm() {
    this.showClientForm = !this.showClientForm;
  }

  startClientEdit() {
    if (!this.selectedClient) {
      return;
    }
    this.editingClient = true;
  }

  cancelClientEdit() {
    if (!this.selectedClient) {
      return;
    }
    this.editingClient = false;
    this.clientEditFormSubmitted = false;
    this.clientEditForm.patchValue({
      cnpj: this.selectedClient.cnpj || '',
      name: this.selectedClient.name || '',
      website: this.selectedClient.website || '',
      segment: this.selectedClient.segment || '',
      description: this.selectedClient.description || '',
      active: this.selectedClient.active ? 'ACTIVE' : 'PROSPECT'
    });
  }

  saveClientEdit() {
    if (!this.selectedClient || !this.tenantId) {
      return;
    }
    this.clientEditFormSubmitted = true;
    if (!this.clientEditForm.valid) {
      this.clientEditForm.markAllAsTouched();
      return;
    }
    const payload = {
      ...this.clientEditForm.value,
      tenant_id: this.tenantId,
      active: this.clientEditForm.value.active === 'ACTIVE' ? 1 : 0
    };
    this.service.updateClient(this.selectedClient.id, payload).subscribe({
      next: () => {
        const updated = {
          ...this.selectedClient,
          ...payload
        };
        this.selectedClient = updated as ControlcliClient;
        const index = this.clients.findIndex((item) => item.id === updated.id);
        if (index !== -1) {
          this.clients[index] = updated as ControlcliClient;
        }
        this.editingClient = false;
        this.clientEditFormSubmitted = false;
        this.loadClients();
      }
    });
  }

  loadClientComments(clientId: number) {
    if (!this.tenantId) {
      return;
    }
    this.service.getComments(this.tenantId, 'CLIENT', clientId).subscribe({
      next: (rows) => (this.clientComments = rows || []),
      error: () => (this.clientComments = [])
    });
  }

  loadProspectComments(prospectId: number) {
    if (!this.tenantId) {
      return;
    }
    this.service.getComments(this.tenantId, 'PROSPECT', prospectId).subscribe({
      next: (rows) => (this.prospectComments = rows || []),
      error: () => (this.prospectComments = [])
    });
  }

  refreshComments(type: 'CLIENT' | 'PROSPECT', id: number) {
    if (type === 'CLIENT' && id === this.selectedClient?.id) {
      this.loadClientComments(id);
    }
    if (type === 'PROSPECT' && id === this.selectedProspect?.id) {
      this.loadProspectComments(id);
    }
  }

  startEditComment(comment: ControlcliComment) {
    this.editingComment = comment;
    this.commentForm.patchValue({
      reference_type: 'CLIENT',
      reference_id: comment.reference_id,
      comment: comment.comment
    });
  }

  saveCommentEdit() {
    if (!this.editingComment || !this.commentForm.value.comment) {
      return;
    }
    this.service.updateComment(this.editingComment.id, { comment: this.commentForm.value.comment }).subscribe(() => {
      if (this.selectedClient) {
        this.loadClientComments(this.selectedClient.id);
      }
      if (this.selectedProspect) {
        this.loadProspectComments(this.selectedProspect.id);
      }
      this.editingComment = null;
      this.commentForm.patchValue({ comment: '' });
    });
  }

  onCommentFileSelected(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0] || null;
    this.commentFile = file;
  }

  onProposalFileSelected(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0] || null;
    this.proposalFile = file;
  }

  get prospectProposals() {
    if (!this.selectedProspect) {
      return [];
    }
    return this.proposals.filter((p) => p.prospect_id === this.selectedProspect!.id);
  }

  get prospectHistory() {
    const entries: any[] = [];
    this.prospectComments.forEach((c) =>
      entries.push({ type: 'COMMENT', text: c.comment, author: c.author_name || 'Usuário', date: c.created_at, attachments: c.attachments || [] })
    );
    this.prospectProposals.forEach((p) =>
      entries.push({
        type: 'PROPOSAL',
        text: p.description || p.proposal_code,
        proposalCode: p.proposal_code,
        proposalVersion: p.proposal_version,
        author: p.sent_by,
        date: p.sent_at || p.valid_until,
        status: p.status,
        attachments: p.attachments || []
      })
    );
    const sorted = entries.sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
    const term = (this.historySearchTerm || '').toLowerCase();
    if (!term) {
      return sorted;
    }
    return sorted.filter((item) => (item.text || '').toLowerCase().includes(term));
  }

  private addProposalAttachment(proposalId: number, attachment: any) {
    const proposals = [...this.proposals];
    const idx = proposals.findIndex((p) => p.id === proposalId);
    if (idx >= 0) {
      const current = proposals[idx];
      const existingAtt = current.attachments || [];
      proposals[idx] = { ...current, attachments: [...existingAtt, attachment] };
      this.proposals = proposals;
    }
  }

  formatAttachmentUrl(path?: string | null) {
    if (!path) {
      return '';
    }
    if (path.startsWith('http')) {
      return path;
    }
    return `${this.fileHostUrl}${path.startsWith('/') ? '' : '/'}${path}`;
  }

  selectProspect(prospect: ControlcliProspect) {
    this.selectedProspect = prospect;
    this.commentForm.patchValue({ reference_type: 'PROSPECT', reference_id: prospect.id });
    this.proposalForm.patchValue({ prospect_id: prospect.id });
    this.prospectDetailForm.reset({
      service_id: prospect.service_id,
      need_description: prospect.need_description || '',
      status: prospect.status
    });
    this.prospectContactForm.reset({ name: '', area: '', role: '', email: '', phone: '' });
    this.showProspectEdit = false;
    this.showProspectContactForm = false;
    this.loadProspectComments(prospect.id);
    this.showProspectDetail = true;
  }

  formatCurrency(value: number | null | undefined) {
    if (value === null || value === undefined) {
      return 'R$ 0,00';
    }
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  translateProspectStatus(status?: string) {
    const map: Record<string, string> = {
      NEW: 'Novo',
      CONTACTED: 'Contato feito',
      PROPOSAL_SENT: 'Proposta enviada',
      NEGOTIATION: 'Negociação',
      SIGNATURE: 'Assinatura',
      WON: 'Ganha',
      LOST: 'Perdida'
    };
    return status ? (map[status] || status) : '—';
  }

  prospectStatusClass(status?: string) {
    const normalized = (status || '').toUpperCase();
    return {
      new: normalized === 'NEW',
      contacted: normalized === 'CONTACTED',
      proposal: normalized === 'PROPOSAL_SENT',
      negotiation: normalized === 'NEGOTIATION',
      signature: normalized === 'SIGNATURE',
      won: normalized === 'WON',
      lost: normalized === 'LOST'
    };
  }

  translateProposalStatus(status?: string) {
    const map: Record<string, string> = {
      SENT: 'Enviada',
      ACCEPTED: 'Aceita',
      REJECTED: 'Recusada',
      EXPIRED: 'Expirada'
    };
    return status ? (map[status] || status) : '—';
  }

  get satisfactionAverage(): number {
    if (!this.surveyResponses.length) {
      return 0;
    }
    const total = this.surveyResponses.reduce((acc, r) => acc + Number(r.score || 0), 0);
    return total / this.surveyResponses.length;
  }

  pipelineCount(status: ControlcliProspect['status']) {
    return this.prospects.filter((p) => p.status === status).length;
  }

  prospectsByStage(status: ControlcliProspect['status']) {
    return this.prospects.filter((p) => p.status === status);
  }

  get hasSurveyResponses() {
    return this.surveyResponses.length > 0;
  }

  get activeClientsCount() {
    return this.clients.filter((c) => c.active).length;
  }

  upcomingProposals(limit = 5) {
    return [...this.proposals]
      .sort((a, b) => new Date(a.valid_until).getTime() - new Date(b.valid_until).getTime())
      .slice(0, limit);
  }

  closeProspectDetail() {
    this.showProspectDetail = false;
    this.selectedProspect = null;
    this.prospectComments = [];
    this.showProspectEdit = false;
    this.showProspectContactForm = false;
    this.commentForm.patchValue({ reference_type: 'PROSPECT', reference_id: '' });
    this.proposalForm.reset({
      status: 'SENT',
      value_total: '',
      value_monthly: '',
      value_annual: '',
      proposal_version: 1,
      prospect_id: ''
    });
    this.disableProposalVersionControl();
  }

  saveProspectDetails() {
    if (!this.selectedProspect || !this.prospectDetailForm.valid) {
      this.prospectDetailForm.markAllAsTouched();
      return;
    }
    const payload = {
      service_id: Number(this.prospectDetailForm.value.service_id),
      need_description: this.prospectDetailForm.value.need_description,
      status: this.prospectDetailForm.value.status
    };
    this.service.updateProspect(this.selectedProspect.id, payload).subscribe({
      next: () => {
        const service = this.services.find((s) => s.id === payload.service_id);
        const updated = {
          ...this.selectedProspect!,
          ...payload,
          service_name: service?.name || this.selectedProspect!.service_name
        };
        this.selectedProspect = updated;
        this.prospects = this.prospects.map((p) => (p.id === updated.id ? { ...p, ...updated } : p));
        this.showProspectEdit = false;
      }
    });
  }

  submitProspectContact() {
    if (!this.tenantId || !this.userId || !this.selectedProspect?.client_id || !this.prospectContactForm.valid) {
      return;
    }
    const payload = {
      ...this.prospectContactForm.value,
      tenant_id: this.tenantId,
      created_by: this.userId
    };
    this.service.createContact(this.selectedProspect.client_id, payload).subscribe({
      next: () => {
        this.prospectContactForm.reset({ name: '', area: '', role: '', email: '', phone: '' });
        this.loadContacts();
      }
    });
  }

  onProposalValueInput(event: Event, controlName: 'value_total' | 'value_monthly' | 'value_annual') {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }
    const digits = input.value.replace(/\D/g, '');
    const formatted = this.formatCurrencyInput(digits);
    this.proposalForm.patchValue({ [controlName]: formatted }, { emitEvent: false });
  }

  private formatCurrencyInput(digits: string) {
    if (!digits) {
      return '';
    }
    const value = Number(digits) / 100;
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  private formatCurrencyValue(value: number | null | undefined) {
    if (value === null || value === undefined) {
      return '';
    }
    return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  private parseCurrencyValue(value: string) {
    if (!value) {
      return 0;
    }
    const normalized = value.replace(/\./g, '').replace(',', '.');
    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
}
